import React, { useMemo } from "react";
import {
  Text,
  TouchableOpacity,
  TouchableOpacityProps,
  StyleSheet,
  StyleProp,
  ViewStyle,
  TextStyle,
  ActivityIndicator,
  View,
  Platform,
} from "react-native";
import Icon from "react-native-vector-icons/MaterialIcons"; // Ensure you have react-native-vector-icons installed
import { useTheme } from "../theme";
import { ThemeColors } from "../theme/types";

interface ButtonProps extends TouchableOpacityProps {
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  isDark?: boolean;
  iconLeading?: string;
  iconTrailing?: string;
  iconSize?: number;
  size?: "small" | "medium" | "large";
  style?: ViewStyle;
  textStyle?: StyleProp<TextStyle>;
  subTextStyle?: StyleProp<TextStyle>;
  text: string;
  subText?: string;
  variant?:
    | "primary"
    | "secondary"
    | "auxiliary"
    | "tertiary"
    | "danger"
    | "primaryInverse"
    | "secondaryInverse"
    | "auxiliaryInverse"
    | "tertiaryInverse"
    | "dangerInverse";
  onPress: () => void;
  icon?: React.ReactElement; // Added prop to support React elements like icons
  children?: React.ReactNode; // Support for custom children
}
// Removed from here as it depends on the 'variant' prop
const sizeMap = {
  small: { paddingVertical: 8, paddingHorizontal: 16, fontSize: 14 },
  medium: { paddingVertical: 10, paddingHorizontal: 20, fontSize: 16 },
  large: { paddingVertical: 12, paddingHorizontal: 24, fontSize: 18 },
};

/**
 * Builds the per-variant color/style maps from centralized theme tokens.
 *
 * Only `primary`, `secondary`, and `danger` map cleanly to existing generic
 * semantic tokens (brand.primary / button.* / status.error). The
 * `auxiliary`, `tertiary`, and all `*Inverse` variants are LhButton-specific
 * accent colors with no equivalent generic semantic role yet, so they
 * intentionally remain literal hex values here (unchanged from before) to
 * avoid inventing speculative component-specific tokens. See Phase 1 report
 * for a note on this as a follow-up decision.
 */
const buildColorMap = (colors: ThemeColors) => ({
  primary: colors.brand.primary,
  secondary: colors.button.secondaryBackground,
  auxiliary: "#117d9f",
  tertiary: "#dc3545",
  danger: colors.status.error,
  primaryInverse: "#ffffff",
  secondaryInverse: "#f8f9fa",
  auxiliaryInverse: "#e9ecef",
  tertiaryInverse: "#f1f3f5",
  dangerInverse: "#fff3cd",
});

const buildVariantMap = (colorMap: ReturnType<typeof buildColorMap>) => ({
  primary: {
    backgroundColor: colorMap.primary,
    borderColor: colorMap.primary,
    borderWidth: 1,
  },
  secondary: {
    backgroundColor: colorMap.secondary,
    borderColor: colorMap.primary,
    borderWidth: 1,
  },
  auxiliary: {
    backgroundColor: colorMap.auxiliary,
    borderColor: colorMap.auxiliary,
    borderWidth: 1,
  },
  tertiary: {
    backgroundColor: colorMap.tertiary,
    borderColor: colorMap.tertiary,
    borderWidth: 1,
  },
  danger: {
    backgroundColor: colorMap.danger,
    borderColor: colorMap.danger,
    borderWidth: 1,
  },
  primaryInverse: {
    backgroundColor: "#ffffff",
    borderColor: "#ffffff",
    borderWidth: 1,
  },
  secondaryInverse: {
    backgroundColor: colorMap.secondaryInverse,
    borderColor: colorMap.secondary,
    borderWidth: 1,
  },
  auxiliaryInverse: {
    backgroundColor: colorMap.auxiliaryInverse,
    borderColor: colorMap.auxiliary,
    borderWidth: 1,
  },
  tertiaryInverse: {
    backgroundColor: colorMap.tertiaryInverse,
    borderColor: colorMap.tertiary,
    borderWidth: 1,
  },
  dangerInverse: {
    backgroundColor: colorMap.dangerInverse,
    borderColor: colorMap.danger,
    borderWidth: 1,
  },
});

const LhButton: React.FC<ButtonProps> = ({
  size = "medium",
  variant = "primary",
  text,
  subText,
  onPress,
  style,
  textStyle,
  subTextStyle,
  isLoading = false,
  disabled = false,
  iconLeading,
  iconTrailing,
  iconSize,
  icon,
  children,
  ...props
}) => {
  const { colors } = useTheme();
  const colorMap = useMemo(() => buildColorMap(colors), [colors]);
  const variantMap = useMemo(() => buildVariantMap(colorMap), [colorMap]);
  const sizeStyles = sizeMap[size];
  const variantStyles = variantMap[variant];
  const lightTextVariants = ["primary", "auxiliary", "tertiary", "danger"]; // secondary removed
  const shouldUseLightText = lightTextVariants.includes(variant);
  // Build text styles first (so lineHeight is known after textStyle overrides)
  const textStyles: TextStyle = {
    color: ["secondary", "primaryInverse"].includes(variant)
      ? colorMap.primary
      : shouldUseLightText
      ? colors.text.inverse
      : colors.text.primary,
    fontFamily: Platform.select({
      ios: 'Inter-Bold',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontWeight: '700',
    fontStyle: 'normal',
    fontSize: sizeStyles.fontSize,
    lineHeight: Math.round(sizeStyles.fontSize * 1.4), // approximate Line Height/Body
    letterSpacing: 0,
    ...StyleSheet.flatten(textStyle),
  };

  // Determine effective button height: prefer explicit style.height, else platform default
  const explicitHeight = (style && (style as any).height) as number | undefined;
  const defaultHeight = Platform.OS === "web" ? 40 : 32;
  // Ensure effective height is at least enough to fit the text lineHeight plus padding
  const minRequiredHeight = (textStyles.lineHeight || 0) + 8;
  const effectiveHeight = Math.max(explicitHeight ?? defaultHeight, minRequiredHeight);

  // Compute paddingVertical to vertically center text without cropping
  const computedPaddingVertical = Math.max(0, Math.floor((effectiveHeight - (textStyles.lineHeight || 0)) / 2));

  const buttonStyles: ViewStyle = {
    ...sizeStyles,
    paddingVertical: computedPaddingVertical,
    ...variantStyles,
    opacity: disabled || isLoading ? 0.6 : 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 20,
    width: Platform.OS == "web" ? "20%" : "100%",
    height: effectiveHeight,
  };
  // const lightTextVariants = ["primary", "auxiliary", "tertiary", "danger"]; // secondary removed
  // const shouldUseLightText = lightTextVariants.includes(variant);
  // (textStyles already defined above)

  return (
    <TouchableOpacity
      onPress={onPress}
      style={[buttonStyles, style]}
      disabled={disabled || isLoading}
      {...props}
    >
      {iconLeading && !isLoading && (
        <Icon
          testID="icon-leading"
          name={iconLeading}
          size={iconSize || sizeStyles.fontSize}
          color={textStyles.color}
          style={{ marginRight: 8 }}
        />
      )}
      {isLoading ? (
        <ActivityIndicator
          testID="loading-indicator"
          size="small"
          color={textStyles.color}
        />
      ) : children ? (
        // Render children if provided
        children
      ) : icon ? (
        // Render custom icon element if provided
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            borderColor: colors.border.default,
          }}
        >
          <Text style={[textStyles, { color: colors.text.inverse }]}>{text}</Text>
          {icon}
        </View>
      ) : (
        // Default text rendering (center vertically to avoid cropping)
        <View style={{ flexDirection: "column", alignItems: "center", justifyContent: 'center' }}>
          <Text style={textStyles}>{text}</Text>
          {subText && (
            <Text
              style={[
                {
                  color: textStyles.color,
                  fontSize: Math.max(10, sizeStyles.fontSize || 14),
                  opacity: 0.8,
                  marginTop: 4,
                },
                subTextStyle,
              ]}
            >
              {subText}
            </Text>
          )}
        </View>
      )}
      {iconTrailing && !isLoading && (
        <Icon
          testID="icon-trailing"
          name={iconTrailing}
          size={iconSize || sizeStyles.fontSize}
          color={textStyles.color}
          style={{ marginLeft: 8 }}
        />
      )}
    </TouchableOpacity>
  );
};

export default LhButton;
